// A short name for a new passkey ("Chrome / macOS"), so the settings list can
// tell devices apart. Browsers do not tell the device's real name; this is a
// guess from the user agent that the person can live with.

interface Source {
  userAgent: string;
  userAgentData?: {
    brands?: { brand: string; version: string }[];
    platform?: string;
  };
}

/** Client Hints mix in made-up brands and "Chromium", which is not a product. */
const NOT_A_PRODUCT = /not.*brand|^chromium$/i;

/** Later names first: Edge also says Chrome. */
const BROWSERS: [RegExp, string][] = [
  [/\bEdg[A-Z]?\//, "Edge"],
  [/\bOPR\//, "Opera"],
  [/\bFirefox\//, "Firefox"],
  [/\bChrome\//, "Chrome"],
  [/\bSafari\//, "Safari"],
];

const PLATFORMS: [RegExp, string][] = [
  [/\bWindows\b/, "Windows"],
  [/\b(iPhone|iPad|iPod)\b/, "iOS"],
  [/\bMac OS X\b/, "macOS"],
  [/\bAndroid\b/, "Android"],
  [/\bLinux\b/, "Linux"],
];

const match = (table: [RegExp, string][], ua: string) =>
  table.find(([re]) => re.test(ua))?.[1];

/** undefined when nothing is known: no name is better than a wrong one. */
export function guessPasskeyName(
  source: Source = navigator as unknown as Source,
): string | undefined {
  const ua = source.userAgent ?? "";
  const brand = source.userAgentData?.brands?.find(
    (b) => !NOT_A_PRODUCT.test(b.brand),
  )?.brand;
  const browser = brand ?? match(BROWSERS, ua);
  const platform = source.userAgentData?.platform || match(PLATFORMS, ua);
  if (browser && platform) return `${browser} / ${platform}`;
  return browser ?? platform;
}

const DISMISSED = "trameria.passkey-prompt-dismissed";

/** "Later" on the passkey page, kept per browser (a passkey is per device). */
export function passkeyPromptDismissed(): boolean {
  try {
    return localStorage.getItem(DISMISSED) === "1";
  } catch {
    return false;
  }
}

export function dismissPasskeyPrompt(): void {
  try {
    localStorage.setItem(DISMISSED, "1");
  } catch {
    // Cannot store (private mode): the page is offered again next time.
  }
}
